import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import eCoachVMSApi from '../../Api';
import { safeJsonParse } from '../../utils/helpFuntion';

//--------------------------------------------------------------
// Helpers for managing token in localStorage / sessionStorage
//--------------------------------------------------------------
const getToken = () => localStorage.getItem('session') || sessionStorage.getItem('session');
const removeToken = () => localStorage.removeItem('session') || sessionStorage.removeItem('session');
const setToken = (token) => localStorage.setItem('session', token);

//==============================================================
// ✅ fetchUserSession - check session from backend
//==============================================================
//
//  Uses stored token to validate user session with backend.
//  - If no token → reject with "No token found"
//  - If token valid → returns session data
//  - If token invalid/expired → clears token, reject with error
//
//==============================================================
export const fetchUserSession = createAsyncThunk('session/fetchUserSession', async (_, { rejectWithValue }) => {
    // get token from storage
    const token = getToken();
    if (!token) {
        return rejectWithValue('No token found');
    }

    try {
        // call backend with token
        const response = await eCoachVMSApi().baseURL.get(`/session/${token}`, {
            withCredentials: true
        });

        // check if token belongs to workshop or main session
        const isWorkshop = token?.startsWith('workshop') || false;
        response.data.session_type = isWorkshop ? 'workshop' : 'main';

        // return session data
        return response.data;
    } catch (error) {
        // clear invalid token
        removeToken();

        // return friendly error messages
        if (error.response && error.response.status === 401) {
            return rejectWithValue('Unauthorized or token expired');
        }
        return rejectWithValue(error.message || 'Failed to fetch session');
    }
});

//==============================================================
// ✅ loginUser - perform login with credentials
//==============================================================
//
//  - Decides correct endpoint (/login or /login-workshop)
//  - Validates username/password
//  - Calls backend, saves session token
//  - Dispatches fetchUserSession() to populate user data
//
//==============================================================
export const loginUser = createAsyncThunk('session/loginUser', async (credentials, { dispatch, rejectWithValue }) => {
    try {
        // choose endpoint depending on session_type
        const endpoint = credentials.session_type === 'workshop' ? '/login-workshop' : '/login';

        // check missing credentials
        if (!credentials.user_name || !credentials.password) {
            return rejectWithValue('Invalid login credentials');
        }

        // call backend with credentials
        const response = await eCoachVMSApi().baseURL.post(endpoint, credentials, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        });

        // save token to localStorage
        setToken(response.data.data.session_id);

        // fetch user session after login
        dispatch(fetchUserSession());

        // return minimal login info
        return {
            session_id: response.data.data.session_id,
            session_type: credentials.session_type
        };
    } catch (error) {
        console.error(error);
        const message = error?.response?.data?.error?.message?.[0];
        const parsed = safeJsonParse(message);

        const errorTitle = parsed?.error?.title;
        const errorDesc = parsed?.error?.userMessage;

        const errorMessage = [errorDesc, errorTitle].filter(Boolean).join(' ') || error?.response?.data?.error || 'Login failed';

        return rejectWithValue(errorMessage);
    }
});

//==============================================================
// ✅ logoutUser - destroy session both client and backend
//==============================================================
//
//  - Sends logout to backend with token
//  - Clears token from storage
//
//==============================================================
export const logoutUser = createAsyncThunk('session/logoutUser', async (_, { dispatch }) => {
    const token = getToken();

    // if token exists → call backend logout
    if (token) {
        await eCoachVMSApi().baseURL.post(`/logout/${token}`, {}, { withCredentials: true });
    }

    // remove token from storage
    removeToken();
});

//--------------------------------------------------------------
// Initial Redux state
//--------------------------------------------------------------
const initialState = {
    user: null,
    error: null,
    permissions: [],
    settings: {},
    isLoading: true,
    sessionType: null
};

//==============================================================
// ✅ sessionSlice - manages login/session state
//==============================================================
//
//  - Handles reducers and async actions for session
//  - Includes load, loadSettings, setLoading
//  - Handles fetchUserSession, loginUser, logoutUser
//
//==============================================================
const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        // load full session (user + permissions + settings)
        load(state, action) {
            state.permissions = action.payload.permissions;
            state.settings = action.payload.settings;
            state.user = action.payload.user;
        },
        // replace only settings
        loadSettings(state, action) {
            state.settings = action.payload;
        },
        // toggle loading flag
        setLoading(state, action) {
            state.isLoading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // update user filter settings fulfilled
            .addCase(updateUserFilterSettings.fulfilled, (state, action) => {
                state.settings = { ...state.settings, ...action.payload };
            })

            // fetchUserSession pending
            .addCase(fetchUserSession.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })

            // fetchUserSession fulfilled
            .addCase(fetchUserSession.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload.user;
                state.permissions = action.payload.permissions || [];
                state.settings = action.payload.settings || [];
                state.error = null;
                state.sessionType = action.payload.session_type;
            })

            // fetchUserSession rejected
            .addCase(fetchUserSession.rejected, (state, action) => {
                state.isLoading = false;
                state.user = null;
                state.permissions = [];
                state.sessionType = null;

                // FIX: only set error if token exists
                if (getToken()) {
                    state.error = {
                        type: 'session',
                        message: action.payload || action.error.message
                    };
                } else {
                    state.error = null;
                }
            })

            // loginUser pending
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })

            // loginUser fulfilled
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.sessionType = action.payload.session_type;
            })

            // loginUser rejected
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.sessionType = null;
                state.error = {
                    type: 'login',
                    message: action.payload || action.error.message
                };
            })

            // logoutUser fulfilled
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null;
                state.sessionType = null;
                state.permissions = [];
                state.isLoading = false;
                state.error = null;
            });
    }
});

//--------------------------------------------------------------
// Selectors - pick data out of state
//--------------------------------------------------------------
export const selectSession = (state) => state?.session;
export const selectUser = createSelector([selectSession], (session) => session?.user);
export const selectPermissions = createSelector([selectSession], (session) => session?.permissions);
export const selectUserSettings = createSelector([selectSession], (session) => session?.settings);
export const selectDailyServiceFilters = createSelector([selectUserSettings], (settings) => settings?.daily_service_filters || {});
export const selectServiceCalendarFilters = createSelector([selectUserSettings], (settings) => settings?.service_calendar_filters || {});
export const selectIsLoading = createSelector([selectSession], (session) => session?.isLoading);

//==============================================================
// ✅ updateUserFilterSettings - save user settings to backend
//==============================================================
//
//  - Pulls current settings from state
//  - Sends update to backend
//  - Returns merged settings_json
//
//==============================================================
export const updateUserFilterSettings = createAsyncThunk('user/updateFilterSettings', async (settings, { getState, rejectWithValue }) => {
    try {
        // get userId from state
        const state = getState();
        const userId = state?.session?.user?.id;

        // merge old settings with new
        const currentSettings = state.session?.settings || {};

        // call backend to update
        const response = await eCoachVMSApi().baseURL.put(`/updateUserSettings/${userId}`, {
            ...currentSettings,
            ...settings
        });

        // return updated settings
        return response.data?.data?.settings_json;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

//--------------------------------------------------------------
// Exports
//--------------------------------------------------------------
export const { load, logout, setLoading, loadSettings } = sessionSlice.actions;
export default sessionSlice.reducer;

